import { INITIAL_CONTRACT_FILTERS } from '../constants/contractsConstants';
import { formatDate as formatDateShared } from '../../../utils/formatUtils';

/**
 * Utilidades para el módulo de contratos
 */

/**
 * Verifica si hay contratos con los criterios especificados
 * @param {Object} counts - Objeto con los conteos de contratos
 * @param {Array} groups - Array de grupos
 * @param {string} planUuid - UUID del plan
 * @param {boolean} isPremium - Si es plan premium
 * @param {string} subscriptionType - Tipo de suscripción ('with_subscribers' o 'no_subscribers')
 * @returns {boolean}
 */
export const hasContractsOfType = (counts, groups, planUuid, isPremium, subscriptionType) => {
  if (!counts || !counts[subscriptionType]) return false;
  // Verificar contratos en grupos
  const hasGroupContracts = groups.some((group) => {
    const groupCounts = counts[subscriptionType][group.uuid] || {};
    const relevantCount = isPremium
      ? groupCounts.premium_count || 0
      : groupCounts.regular_count || 0;

    return group.uuid_plan === planUuid && relevantCount > 0;
  });

  // Verificar contratos sin grupo (solo para no_subscribers)
  if (subscriptionType === 'no_subscribers') {
    const noGroupCounts = counts.no_group?.[planUuid] || {};
    const relevantNoGroupCount = isPremium
      ? noGroupCounts.premium_count || 0
      : noGroupCounts.regular_count || 0;

    const hasNonGroupContracts = relevantNoGroupCount > 0;
    return hasGroupContracts || hasNonGroupContracts;
  }

  return hasGroupContracts;
};

/**
 * Genera el nombre del plan con el sufijo Premium si corresponde
 * @param {Object} plan - Objeto del plan
 * @param {boolean} isPremium - Si es plan premium
 * @returns {string}
 */
export const getPlanDisplayName = (plan, isPremium) => {
  return isPremium ? `${plan.name} Premium` : plan.name;
};

/**
 * Construye los filtros para obtener contratos
 * @param {string} planUuid - UUID del plan
 * @param {boolean} isPremium - Si es plan premium
 * @param {boolean} hasSubscribers - Si tiene suscriptores
 * @param {boolean} archived - Si está archivado
 * @returns {Object}
 */
export const buildContractFilters = (planUuid, isPremium, hasSubscribers, archived = false) => {
  return {
    archived,
    uuid_plan: planUuid,
    has_subscribers: hasSubscribers,
    is_premium: isPremium,
  };
};


/**
 * Formatea una fecha para mostrar en la UI
 * @param {string} dateString - Fecha en formato string
 * @returns {string}
 */
export const formatDateForDisplay = (dateString) =>
  formatDateShared(dateString, {
    options: { day: 'numeric', month: 'long', year: 'numeric' },
    fallback: '-',
  });

/**
 * Formatea una fecha para input type="date"
 * @param {string} dateString - Fecha en formato string
 * @returns {string}
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  return dateString.substr(0, 10);
};

/**
 * Valida si un valor booleano está activo
 * @param {any} value - Valor a validar
 * @returns {boolean}
 */
export const isBooleanActive = (value) => {
  return value === 'True' || value === true;
};

/**
 * Convierte un valor booleano para envío al servidor
 * @param {boolean} value - Valor booleano
 * @returns {boolean}
 */
export const normalizeBooleanValue = (value) => {
  return Boolean(value);
};

/**
 * Obtiene el nombre de un elemento por UUID
 * @param {Array} items - Array de elementos
 * @param {string} uuid - UUID a buscar
 * @param {string} nameField - Campo que contiene el nombre (por defecto 'name')
 * @returns {string}
 */
export const getNameByUuid = (items, uuid, nameField = 'name') => {
  if (!items || !uuid) return '-';
  const item = items.find((item) => item.uuid === uuid);
  return item ? item[nameField] : '-';
};

/**
 * Filtra contratos por término de búsqueda
 * @param {Array} contracts - Array de contratos
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Array}
 */
export const filterContractsBySearch = (contracts, searchTerm) => {
  if (!searchTerm || !contracts) return contracts;

  const term = searchTerm.toUpperCase();

  return contracts.filter((contract) => {
    // Buscar por número de contrato
    if (contract.contract_number && contract.contract_number.includes(term)) {
      return true;
    }

    // Buscar por DNI de suscriptores
    if (
      contract.subscribers &&
      contract.subscribers.some(
        (sub) =>
          sub.subscriber && sub.subscriber.dni && sub.subscriber.dni.toUpperCase().includes(term)
      )
    ) {
      return true;
    }

    // Buscar por nombre o apellido de suscriptores
    if (
      contract.subscribers &&
      contract.subscribers.some((sub) => {
        if (!sub.subscriber) return false;
        const firstName = (sub.subscriber.first_name || '').toUpperCase();
        const lastName = (sub.subscriber.last_name || '').toUpperCase();
        return firstName.includes(term) || lastName.includes(term);
      })
    ) {
      return true;
    }

    return false;
  });
};

/**
 * Aplica filtros múltiples a los contratos
 * @param {Array} contracts - Array de contratos original
 * @param {Object} filters - Objeto con los filtros
 * @returns {Array}
 */
export const applyContractFilters = (contracts, filters) => {
  if (!contracts) return [];

  let filtered = [...contracts];

  // Filtro por grupo
  if (filters && filters.groupUuid && filters.groupUuid !== 0) {
    filtered = filtered.filter((contract) => contract.uuid_group === filters.groupUuid);
  }

  // Filtro por número de contrato
  if (filters && filters.contractNumber) {
    filtered = filtered.filter(
      (contract) =>
        contract.contract_number && contract.contract_number.includes(filters.contractNumber)
    );
  }

  // Filtro por DNI
  if (filters && filters.dni) {
    filtered = filtered.filter((contract) => {
      if (!contract.subscribers || !Array.isArray(contract.subscribers)) return false;
      return contract.subscribers.some(
        (sub) =>
          sub.subscriber &&
          sub.subscriber.dni &&
          sub.subscriber.dni.toUpperCase().includes(filters.dni.toUpperCase())
      );
    });
  }

  // Filtro por nombre
  if (filters && filters.name) {
    filtered = filtered.filter((contract) => {
      if (!contract.subscribers || !Array.isArray(contract.subscribers)) return false;
      return contract.subscribers.some((sub) => {
        if (!sub.subscriber) return false;
        const firstName = (sub.subscriber.first_name || '').toUpperCase();
        const lastName = (sub.subscriber.last_name || '').toUpperCase();
        const searchName = filters.name.toUpperCase();
        return firstName.includes(searchName) || lastName.includes(searchName);
      });
    });
  }

  return filtered;
};

/**
 * Calcula el total de páginas para paginación
 * @param {number} totalItems - Total de elementos
 * @param {number} itemsPerPage - Elementos por página
 * @returns {number}
 */
export const calculateTotalPages = (totalItems, itemsPerPage) => {
  return Math.ceil(totalItems / itemsPerPage);
};

/**
 * Obtiene los elementos de la página actual
 * @param {Array} items - Array de elementos
 * @param {number} page - Página actual (1-indexed)
 * @param {number} itemsPerPage - Elementos por página
 * @returns {Array}
 */
export const getCurrentPageItems = (items, page, itemsPerPage) => {
  if (!Array.isArray(items) || items.length === 0) return [];

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return items.slice(startIndex, endIndex);
};


/**
 * Retorna una copia de los filtros iniciales
 * @returns {Object}
 */
export const getInitialContractFilters = () => {
  return { ...INITIAL_CONTRACT_FILTERS };
};

/**
 * Actualiza un filtro específico
 * @param {Object} currentFilters - Filtros actuales
 * @param {string} name - Nombre del filtro
 * @param {any} value - Nuevo valor
 * @returns {Object}
 */
export const updateContractFilter = (currentFilters, name, value) => {
  return {
    ...currentFilters,
    [name]: value,
  };
};

/**
 * Resetea todos los filtros
 * @returns {Object}
 */
export const resetContractFilters = () => {
  return { ...INITIAL_CONTRACT_FILTERS };
};
